import _ from 'lodash';
import { useSelector } from 'react-redux';
import {
  PRIVACY_ALL_PRIVATE_TYPES,
  PRIVACY_ALL_PRIVATE_HIDDEN,
  RESTRICT_CASE_ACTIONS,
} from '@app/constants/permissions';
import { UNASSIGNED_OPERATOR } from '@app/constants/general';

export const isPrivateCase = (job) => {
  const casePrivacy = _.get(job, 'privacy', '');
  return casePrivacy.match(/private*/) !== null;
};

export const isPrivateOrUnassigned = (job, primaryOperatorId) => {
  const isPrivate = isPrivateCase(job);
  return isPrivate && primaryOperatorId === UNASSIGNED_OPERATOR;
};

export const useBuildCaseVisibilityList = () => {
  const currentPermissions = useSelector((state) =>
    _.get(state, 'auth.permissions', []),
  );
  let radioButtonValues = [
    { id: 'public', label: 'Public' },
    { id: 'private-redacted', label: 'Private' },
  ];
  if (currentPermissions.includes(PRIVACY_ALL_PRIVATE_HIDDEN)) {
    radioButtonValues = [
      { id: 'public', label: 'Public' },
      { id: 'private-hidden', label: 'Private' },
    ];
  } else if (currentPermissions.includes(PRIVACY_ALL_PRIVATE_TYPES)) {
    radioButtonValues = [
      { id: 'public', label: 'Public' },
      { id: 'private-hidden', label: 'Private - Hidden' },
      { id: 'private-redacted', label: 'Private - Redacted' },
    ];
  }
  return radioButtonValues;
};

export const sortCases = (jobs, authId, currentPermissions, presetSort) =>
  jobs?.sort(
    (a, b) =>
      (currentPermissions?.includes('NEEDS_ATTENTION') &&
        (b?.needsAttentionMessageCount || 0) -
          (a?.needsAttentionMessageCount || 0)) ||
      (currentPermissions?.includes('NEEDS_ACTION') &&
        (b?.needsAction?.hasQuestionMessageCount || 0) -
          (a?.needsAction?.hasQuestionMessageCount || 0)) ||
      (currentPermissions?.includes('NOTES_ACCESS') &&
        (a?.unreadNoteMentions?.[authId] === undefined) -
          (b?.unreadNoteMentions?.[authId] === undefined)) ||
      (presetSort === 'ASC'
        ? (a?.latestActivity?.raw || 0) - (b?.latestActivity?.raw || 0)
        : (b?.latestActivity?.raw || 0) - (a?.latestActivity?.raw || 0)),
  );

const caseAccessVariables = (
  participants,
  activeCase,
  currentPermissions,
  currentUserId,
) => {
  const isCurrentUserOperator = activeCase.operatorId === currentUserId;
  const isCurrentUserParticipant = participants.find(
    (p) => p.id === currentUserId,
  );
  const hasUserOverride = currentPermissions.includes(
    'OVERRIDE_CASE_VISIBILITY',
  );
  const isOperatorUnassigned =
    !_.has(activeCase, 'operatorId') ||
    activeCase.operatorId === 'undefined' ||
    activeCase.operatorId === UNASSIGNED_OPERATOR;

  return {
    isOperatorUnassigned,
    isCurrentUserOperator,
    isCurrentUserParticipant,
    hasUserOverride,
  };
};

export const isCaseVisibilityDisabled = (
  participants,
  activeCase,
  currentPermissions,
  currentUserId,
) => {
  // Changing case visibility is a case action (an operational state change), so a
  // role restricted from case actions can never change it — even when assigned, a
  // participant, or holding an override.
  if (currentPermissions.includes(RESTRICT_CASE_ACTIONS)) {
    return true;
  }

  const {
    isOperatorUnassigned,
    isCurrentUserOperator,
    isCurrentUserParticipant,
    hasUserOverride,
  } = caseAccessVariables(
    participants,
    activeCase,
    currentPermissions,
    currentUserId,
  );

  let result = true;
  if (isOperatorUnassigned) {
    result = true;
  } else if (
    hasUserOverride ||
    isCurrentUserParticipant ||
    isCurrentUserOperator
  ) {
    result = false;
  }
  return result;
};

export const checkForCaseAccess = (item, userId, currentPermissions) => {
  const hasUserOverride = currentPermissions.includes(
    'OVERRIDE_CASE_VISIBILITY',
  );
  const selectedPrivacy = item?.privacy;
  const operatorId = item?.assignedOperator?.id || item?.operatorId?._id;
  const secondaryOperatorIds = item?.secondaryOperatorIds;
  const isUserSecondaryOperator = () =>
    secondaryOperatorIds ? secondaryOperatorIds.includes(userId) : false;

  if (
    hasUserOverride ||
    selectedPrivacy === 'public' ||
    userId === operatorId ||
    isUserSecondaryOperator(item)
  ) {
    return true;
  }
  return false;
};

export const useCheckForCaseAccess = (item, userId, currentPermissions) => {
  if (!userId) {
    // eslint-disable-next-line no-param-reassign, react-hooks/rules-of-hooks
    userId = useSelector((state) => _.get(state, 'auth.user._id', []));
  }
  if (!currentPermissions) {
    // eslint-disable-next-line no-param-reassign, react-hooks/rules-of-hooks
    currentPermissions = useSelector((state) =>
      _.get(state, 'auth.permissions', []),
    );
  }

  return checkForCaseAccess(item, userId, currentPermissions);
};

export const handleUserAssignment =
  (activeCase, assignJob, dispatch, toaster) => (primaryOperator) => {
    const primaryOperatorId =
      typeof primaryOperator === 'string'
        ? primaryOperator
        : primaryOperator.id;
    if (isPrivateOrUnassigned(activeCase, primaryOperatorId)) {
      toaster.create({
        title: 'You cannot mark a private case as unassigned.',
        type: 'error',
        duration: 5000,
      });
      return Promise.resolve();
    }
    return dispatch(
      assignJob(
        { ...activeCase, operatorId: primaryOperatorId },
        null,
        true,
        toaster,
      ),
    );
  };

export const handleQueueAssignment =
  (activeCase, updateJob, dispatch, toaster) => (queueId) => {
    return dispatch(updateJob({ ...activeCase, queueId }, null, true, toaster));
  };

export const bulkUpdateHelper = (jobs, property, newValue) => {
  const jobsToUpdate = jobs.map((jobToUpdate) => {
    const changes = {};
    changes[`${property}`] = newValue;
    return { ...jobToUpdate, ...changes };
  });
  return jobsToUpdate;
};
